import React, { useEffect, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { styled } from '@components-shared/src/stitches';
import { seek, useAudioController } from '@store/audio/AudioController';
import { useAudioPosition } from '@store/audio/AudioPosition';
import { formatTime } from './utils';

const THUMB_BOX_SHADOW = '-1340px -1341px 0 1340px #fff, -1340px -1337px 0 1340px #EB5CBB';

export const Timeline = () => {
  const { duration, playing } = useAudioController();
  const { position } = useAudioPosition();
  const [scrubPosition, setSrcubPosition] = useState<number>(position);

  // To create a smooth user experience, detach from the live position when user is seeking
  // Then return to updating in real time after user is done seeking or when unpausing
  const [uncontrolled, setUncontrolled] = useState(false);

  const startSeek = () => {
    setUncontrolled(true);
  };

  const endSeek = (event: SyntheticEvent) => {
    if (playing) setUncontrolled(false);
    const target = event.target as HTMLInputElement;
    seek(target.valueAsNumber);
  };

  useEffect(() => {
    if (playing) setUncontrolled(false);
  }, [playing]);

  return (
    <div className="flex w-full items-center justify-center">
      {/* scrubbing the input does not update the position of the song until song is played so this makes sure that duration still updates on scrub */}
      <span className="mr-2 min-w-[28px] text-right font-base text-[10px] text-base-xs font-semibold text-neutral500">
        {formatTime(uncontrolled ? scrubPosition : position)}
      </span>
      <Input
        type="range"
        step={1}
        min={0}
        value={uncontrolled ? scrubPosition : position}
        max={duration}
        onMouseDown={startSeek}
        onTouchStart={startSeek}
        onKeyDown={startSeek}
        onMouseUp={endSeek}
        onTouchEnd={endSeek}
        onKeyUp={endSeek}
        onChange={e => {
          setSrcubPosition(e.target.valueAsNumber);
        }}
      />
      <span className="ml-2 min-w-[28px] text-left font-base text-[10px] text-base-xs font-semibold text-neutral500">
        {formatTime(duration)}
      </span>
    </div>
  );
};

const Input = styled('input', {
  WebkitAppearance: 'none',
  borderRadius: 2,
  cursor: 'pointer',
  overflow: 'hidden',
  width: '100%',
  '&::-webkit-slider-runnable-track': {
    height: '4px',
    background: '$grey200',
  },
  '&::-moz-range-track': {
    height: '4px',
    background: '$grey200',
  },
  '&::-webkit-slider-thumb': {
    WebkitAppearance: 'none',
    backgroundColor: '$fuchsia500',
    boxShadow: THUMB_BOX_SHADOW,
    height: '1px',
    width: '1px',
  },
  '&::-moz-range-thumb': {
    backgroundColor: '$fuchsia500',
    border: 'none',
    height: '1px',
    width: '1px',
  },
  '&::-moz-range-progress': {
    background: '$fuchsia500',
  },
});
